/**
 * Homepage hero carousel — CSS scroll-snap + lightweight controls.
 * Rewinds first↔last without cloning slides.
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 767px)';
  var AUTOPLAY_MS = 5500;
  var INSTANCE_KEY = '_heroCarousel';
  var FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function supportsInert() {
    return typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;
  }

  function HeroCarousel(root) {
    this.root = root;
    this.track = root.querySelector('.hero-carousel__track');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.hero-carousel__slide'));
    this.prevButton = root.querySelector('.hero-carousel__button--prev');
    this.nextButton = root.querySelector('.hero-carousel__button--next');
    this.pagination = root.querySelector('.hero-carousel__pagination');
    this.bullets = [];
    this.index = 0;
    this.userInteracted = false;
    this.autoplayTimer = null;
    this.scrollRaf = null;
    this.resizeRaf = null;
    this.isInView = true;
    this.isPointerDown = false;
    this.isHovered = false;
    this.isFocusWithin = false;
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.mobileMq = window.matchMedia(MOBILE_MQ);
    this.onMediaChange = this.updateNavVisibility.bind(this);
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerEnd = this.handlePointerEnd.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onPrevClick = this.handlePrevClick.bind(this);
    this.onNextClick = this.handleNextClick.bind(this);
    this.onMouseEnter = this.handleMouseEnter.bind(this);
    this.onMouseLeave = this.handleMouseLeave.bind(this);
    this.onFocusIn = this.handleFocusIn.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);

    if (!this.track || this.slides.length === 0) return;

    this.setupRegion();
    this.setupPagination();
    this.bindControls();
    this.bindScroll();
    this.bindVisibility();
    this.bindHoverPause();
    this.bindIntersection();
    this.bindResize();
    this.updateUI();
    this.scheduleAutoplay();

    this.mobileMq.addEventListener('change', this.onMediaChange);
  }

  HeroCarousel.prototype.setupRegion = function () {
    if (!this.root.getAttribute('role')) {
      this.root.setAttribute('role', 'region');
    }
    if (!this.root.getAttribute('aria-roledescription')) {
      this.root.setAttribute('aria-roledescription', 'carousel');
    }
    if (!this.root.getAttribute('aria-label')) {
      this.root.setAttribute('aria-label', 'Homepage featured promotions');
    }
    this.root.setAttribute('tabindex', '0');
  };

  HeroCarousel.prototype.setupPagination = function () {
    this.bullets = [];
    if (!this.pagination || this.slides.length < 2) return;

    this.pagination.innerHTML = '';

    var self = this;
    this.slides.forEach(function (slide, i) {
      var bullet = document.createElement('button');
      bullet.type = 'button';
      bullet.className = 'hero-carousel__bullet';
      bullet.setAttribute(
        'aria-label',
        slide.getAttribute('data-banner-heading') || 'Go to slide ' + (i + 1)
      );
      bullet.addEventListener('click', function () {
        self.markInteracted();
        self.goTo(i);
      });
      self.pagination.appendChild(bullet);
      self.bullets.push(bullet);
    });
  };

  HeroCarousel.prototype.handlePrevClick = function () {
    this.markInteracted();
    this.prev();
  };

  HeroCarousel.prototype.handleNextClick = function () {
    this.markInteracted();
    this.next();
  };

  HeroCarousel.prototype.handleMouseEnter = function () {
    this.isHovered = true;
    this.stopAutoplay();
  };

  HeroCarousel.prototype.handleMouseLeave = function () {
    this.isHovered = false;
    this.scheduleAutoplay();
  };

  HeroCarousel.prototype.handleFocusIn = function () {
    this.isFocusWithin = true;
    this.stopAutoplay();
  };

  HeroCarousel.prototype.handleFocusOut = function (event) {
    var next = event.relatedTarget;
    if (next && this.root.contains(next)) return;
    this.isFocusWithin = false;
    this.scheduleAutoplay();
  };

  HeroCarousel.prototype.handlePointerDown = function (event) {
    if (event.target.closest('a, button, input, select, textarea, label')) {
      return;
    }
    
    this.isPointerDown = true;
    this.markInteracted();
    this.stopAutoplay();
    if (typeof this.track.setPointerCapture === 'function' && event.pointerId != null) {
      try {
        this.track.setPointerCapture(event.pointerId);
      } catch (err) {
        // Ignore capture failures on unsupported targets.
      }
    }
  };

  HeroCarousel.prototype.handlePointerEnd = function () {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.scheduleAutoplay();
  };

  HeroCarousel.prototype.handleKeydown = function (event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.markInteracted();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.markInteracted();
      this.next();
    }
  };

  HeroCarousel.prototype.handleScroll = function () {
    var self = this;
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(function () {
      self.scrollRaf = null;
      if (self.syncIndexFromScroll()) {
        self.updateUI();
      }
    });
  };

  HeroCarousel.prototype.handleVisibilityChange = function () {
    if (document.hidden) {
      this.stopAutoplay();
    } else {
      this.scheduleAutoplay();
    }
  };

  HeroCarousel.prototype.bindControls = function () {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onPrevClick);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextClick);
    }

    this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.track.addEventListener('pointerup', this.onPointerEnd, { passive: true });
    this.track.addEventListener('pointercancel', this.onPointerEnd, { passive: true });
    this.track.addEventListener('lostpointercapture', this.onPointerEnd, { passive: true });
    this.root.addEventListener('keydown', this.onKeydown);
  };

  HeroCarousel.prototype.bindHoverPause = function () {
    this.root.addEventListener('mouseenter', this.onMouseEnter);
    this.root.addEventListener('mouseleave', this.onMouseLeave);
    // Keyboard equivalent of hover: pause while focus is inside the carousel.
    this.root.addEventListener('focusin', this.onFocusIn);
    this.root.addEventListener('focusout', this.onFocusOut);
  };

  HeroCarousel.prototype.bindScroll = function () {
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
  };

  HeroCarousel.prototype.bindVisibility = function () {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  };

  HeroCarousel.prototype.bindIntersection = function () {
    var self = this;

    if (typeof IntersectionObserver === 'undefined') {
      this.isInView = true;
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        self.isInView = !!(entry && entry.isIntersecting);
        if (self.isInView) {
          self.scheduleAutoplay();
        } else {
          self.stopAutoplay();
        }
      },
      { threshold: 0.25 }
    );

    this.intersectionObserver.observe(this.root);
  };

  HeroCarousel.prototype.bindResize = function () {
    var self = this;

    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(function () {
      if (self.resizeRaf) cancelAnimationFrame(self.resizeRaf);
      self.resizeRaf = requestAnimationFrame(function () {
        self.resizeRaf = null;
        var slide = self.slides[self.index];
        if (!slide) return;
        self.track.scrollLeft = slide.offsetLeft;
      });
    });

    this.resizeObserver.observe(this.track);
  };

  HeroCarousel.prototype.syncIndexFromScroll = function () {
    var scrollLeft = this.track.scrollLeft;
    var closestIndex = 0;
    var closestDistance = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var distance = Math.abs(this.slides[i].offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    if (closestIndex === this.index) return false;

    this.index = closestIndex;
    return true;
  };

  HeroCarousel.prototype.canAutoplay = function () {
    return (
      !prefersReducedMotion() &&
      this.slides.length > 1 &&
      !document.hidden &&
      this.isInView &&
      !this.isPointerDown &&
      !this.isHovered &&
      !this.isFocusWithin
    );
  };

  HeroCarousel.prototype.goTo = function (index, instant) {
    if (!this.slides.length) return;

    var len = this.slides.length;
    if (index < 0) index = len - 1;
    if (index >= len) index = 0;

    this.index = index;
    var left = this.slides[index].offsetLeft;

    if (instant || prefersReducedMotion()) {
      this.track.scrollLeft = left;
    } else if (typeof this.track.scrollTo === 'function') {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }

    this.updateUI();
    this.scheduleAutoplay();
  };

  HeroCarousel.prototype.next = function () {
    this.goTo(this.index + 1);
  };

  HeroCarousel.prototype.prev = function () {
    this.goTo(this.index - 1);
  };

  HeroCarousel.prototype.markInteracted = function () {
    if (this.userInteracted) return;
    this.userInteracted = true;
    this.root.classList.add('hero-carousel--user-interacted');
    this.updateNavVisibility();
  };

  HeroCarousel.prototype.updateNavVisibility = function () {
    if (!this.prevButton || !this.nextButton) return;

    this.nextButton.classList.remove('hero-carousel__button--hidden');

    if (this.mobileMq.matches || this.userInteracted) {
      this.prevButton.classList.remove('hero-carousel__button--hidden');
      return;
    }

    if (this.index === 0) {
      this.prevButton.classList.add('hero-carousel__button--hidden');
    } else {
      this.prevButton.classList.remove('hero-carousel__button--hidden');
    }
  };

  HeroCarousel.prototype.setSlideFocusables = function (slide, active) {
    var nodes = slide.querySelectorAll(FOCUSABLE_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (active) {
        if (el.hasAttribute('data-hero-tabindex')) {
          var prev = el.getAttribute('data-hero-tabindex');
          if (prev === '') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', prev);
          el.removeAttribute('data-hero-tabindex');
        }
      } else if (!el.hasAttribute('data-hero-tabindex')) {
        el.setAttribute(
          'data-hero-tabindex',
          el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : ''
        );
        el.setAttribute('tabindex', '-1');
      }
    }
  };

  HeroCarousel.prototype.updateSlideAccessibility = function () {
    var useInert = supportsInert();

    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var active = i === this.index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');

      if (useInert) {
        slide.inert = !active;
      } else {
        this.setSlideFocusables(slide, active);
      }
    }
  };

  HeroCarousel.prototype.updateAria = function () {
    var len = this.slides.length;
    var nextSlide = this.slides[(this.index + 1) % len];
    var prevSlide = this.slides[(this.index - 1 + len) % len];

    if (this.nextButton && nextSlide) {
      var nextHeading = nextSlide.getAttribute('data-banner-heading');
      this.nextButton.setAttribute(
        'aria-label',
        nextHeading ? 'Next slide: ' + nextHeading : 'Next slide'
      );
    }
    if (this.prevButton && prevSlide) {
      var prevHeading = prevSlide.getAttribute('data-banner-heading');
      this.prevButton.setAttribute(
        'aria-label',
        prevHeading ? 'Previous slide: ' + prevHeading : 'Previous slide'
      );
    }

    for (var i = 0; i < this.bullets.length; i++) {
      var bullet = this.bullets[i];
      var isActive = i === this.index;
      bullet.classList.toggle('hero-carousel__bullet--active', isActive);
      bullet.setAttribute('aria-current', isActive ? 'true' : 'false');
    }

    this.updateSlideAccessibility();
  };

  HeroCarousel.prototype.updateUI = function () {
    this.updateNavVisibility();
    this.updateAria();
  };

  HeroCarousel.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  HeroCarousel.prototype.scheduleAutoplay = function () {
    var self = this;
    this.stopAutoplay();
    if (!this.canAutoplay()) return;

    this.autoplayTimer = window.setTimeout(function () {
      self.autoplayTimer = null;
      if (!self.canAutoplay()) return;
      self.next();
    }, AUTOPLAY_MS);
  };

  HeroCarousel.prototype.destroy = function () {
    this.stopAutoplay();

    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.mobileMq && this.onMediaChange) {
      this.mobileMq.removeEventListener('change', this.onMediaChange);
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointerup', this.onPointerEnd);
      this.track.removeEventListener('pointercancel', this.onPointerEnd);
      this.track.removeEventListener('lostpointercapture', this.onPointerEnd);
    }

    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.onPrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.onNextClick);
    }
    if (this.root) {
      this.root.removeEventListener('keydown', this.onKeydown);
      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      this.root.removeAttribute('tabindex');
      delete this.root.dataset.heroCarouselInit;
      this.root[INSTANCE_KEY] = null;
    }

    this.bullets = [];
  };

  function initRoot(root) {
    if (!root || root.dataset.heroCarouselInit === 'true') return;
    root.dataset.heroCarouselInit = 'true';
    root[INSTANCE_KEY] = new HeroCarousel(root);
  }

  function init() {
    initRoot(document.querySelector('.hero-carousel'));
  }

  function onSectionUnload(event) {
    var root = event.target.querySelector('.hero-carousel');
    if (root && root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
  }

  function onSectionLoad(event) {
    initRoot(event.target.querySelector('.hero-carousel'));
  }

  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:load', onSectionLoad);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
